import React, { useEffect, useMemo } from "react";
import {
  Button,
  Card,
  Typography,
  InputLabel,
  TextField,
  Autocomplete,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import InlineDropdown from "../../common/InlineDropdown/InlineDropdown";
import ColumnGroupingTable from "../../table/ColumnGroupingTable.component";
import useStyles from "./SensitivityGAPGlobal.styles";
import SensitivityGAPGlobalExport from "./SensitivityGAPGlobalExport";
import { useHedgeRatioContext } from "../../../context/HedgeRatioContext";
import { useUserContext } from "../../../context/UserContext";
import { getMappedBusinessEntityIDs } from "../../../utils/utils";

const mapScenarioMnemonics = (scenarioMnemonic) => ({
  id: scenarioMnemonic?.mnemonic,
  name: scenarioMnemonic?.mnemonic,
});

const mapFigureDivisor = (figureDivisor) => ({
  id: figureDivisor.value,
  name: figureDivisor.name,
});

const mapDecimalsIn = (decimalsIn) => ({
  id: decimalsIn.value,
  name: decimalsIn.name,
});

const mapModelDatabase = (modelDatabase) => ({
  id: modelDatabase.modelName,
  name: modelDatabase.modelName,
});

const flattenReportData = (
  nestedData,
  selectedFigureDivisor,
  selectedDecimalsIn,
) => {
  if (!nestedData || !Array.isArray(nestedData)) {
    return [];
  }

  const divisor = parseFloat(selectedFigureDivisor) || 1;
  const shouldDivide = divisor !== 1;

  const decimals =
    selectedDecimalsIn !== null && selectedDecimalsIn !== undefined
      ? parseInt(selectedDecimalsIn, 10)
      : null;
  const shouldFormatDecimals = decimals !== null && !isNaN(decimals);

  return nestedData.map((row) => {
    const { datas, ...fixedProps } = row;
    const formattedDatas = {};

    if (datas) {
      for (const key in datas) {
        if (Object.hasOwnProperty.call(datas, key)) {
          let value = datas[key];

          if (shouldDivide) {
            value = value / divisor;
          }

          if (shouldFormatDecimals) {
            formattedDatas[key] = value.toFixed(decimals);
          } else {
            formattedDatas[key] = value;
          }
        }
      }
    }

    return {
      ...fixedProps,
      ...formattedDatas,
    };
  });
};

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2),
  textAlign: "center",
  color: theme.palette.text.primary,
}));

const SensitivityGAPGlobalFiltersAndReports = () => {
  const { classes } = useStyles();
  const { selectedUserDetailLoading, selectedUserDetail } = useUserContext();

  const {
    hrFiltersData,
    hrFiltersDataLoading,
    selectedBusinessEntityID,
    setSelectedBusinessEntityID,
    localCurrency,
    setLocalCurrency,
    selectedModelDatabase,
    setSelectedModelDatabase,
    modelDatabases,
    setModelDatabases,
    scenarioMnemonicsData,
    selectedScenarioMnemonics,
    setSelectedScenarioMnemonics,
    figureDivisors,
    selectedFigureDivisor,
    setSelectedFigureDivisor,
    decimalsIn,
    selectedDecimalsIn,
    setSelectedDecimalsIn,

    sensitivityGapData,
    setSensitivityGapData,
    sensitivityGapDataLoading,
    sensitivityGapDataError,

    fetchSensitivityGapResultReport,
  } = useHedgeRatioContext();

  const { regions } = selectedUserDetail || {};

  const allsfcs = useMemo(() => {
    return (
      regions
        ?.flatMap((region) => region?.sfcs?.map((sfc) => sfc) || [])
        .sort((a, b) => a.businessEntityID.localeCompare(b.businessEntityID)) ||
      []
    );
  }, [regions]);

  const mappedBusinessEntityIDs = getMappedBusinessEntityIDs(
    hrFiltersData,
    regions,
  );

  const mappedFigureDivisors = [
    ...(figureDivisors || []).map(mapFigureDivisor),
  ];

  const mappedDecimalsIn = [...(decimalsIn || []).map(mapDecimalsIn)];

  useEffect(() => {
    const entityObject = hrFiltersData?.find(
      (item) => item.entity === selectedBusinessEntityID,
    );
    setModelDatabases(entityObject?.dateModels || []);
    const { localCurrency: currency } =
      allsfcs?.find(
        (sfc) => sfc.businessEntityID === selectedBusinessEntityID,
      ) || {};
    setLocalCurrency(currency);
  }, [
    selectedBusinessEntityID,
    hrFiltersData,
    setModelDatabases,
    allsfcs,
    setLocalCurrency,
  ]);

  const mappedModelDatabases = [
    ...(modelDatabases || []).map(mapModelDatabase),
  ];

  const mappedScenarioMnemonics = [
    ...(scenarioMnemonicsData || []).map(mapScenarioMnemonics),
  ];
  const handleSubmitBtn = () => {
    if (selectedScenarioMnemonics) {
      fetchSensitivityGapResultReport();
    } else {
      setSensitivityGapData([]);
    }
  };

  const dynamicColumns =
    sensitivityGapData &&
    sensitivityGapData?.length > 0 &&
    sensitivityGapData?.[0]?.datas
      ? Object.keys(sensitivityGapData?.[0]?.datas)?.map((key) => {
          return {
            id: key,
            label: key,
            minWidth: 100,
            align: "right",
          };
        })
      : [];

  const columns = [
    {
      id: "formattedAccountName",
      label: "Formatted Account Name",
      minWidth: 280,
      align: "left",
    },
    ...dynamicColumns,
  ];

  const exportColumns = [
    { id: "accountNum", label: "Account Number", minWidth: 60, align: "left" },
    { id: "accountName", label: "Account Name", minWidth: 200, align: "left" },
    {
      id: "formattedAccountName",
      label: "Formatted Account Name",
      minWidth: 280,
      align: "left",
    },
    {
      id: "formattedAccountName",
      label: "Formatted Account Name",
      minWidth: 130,
      align: "left",
    },
    ...dynamicColumns,
  ];

  const preprocessedData = flattenReportData(
    sensitivityGapData,
    selectedFigureDivisor,
    selectedDecimalsIn,
  );

  const preprocessedDataExport = flattenReportData(
    sensitivityGapData,
    null,
    null,
  );

  return (
    <Card
      style={{
        padding: "6px",
        marginBottom: "20px",
        width: "100%",
      }}
    >
      <Typography
        sx={{
          color: "#6a6a6a",
          marginBottom: 2,
          fontWeight: "bold",
        }}
      >
        The below data shows the Sensitivity GAP Report (Modeler Global View)
        for the selected filters.
      </Typography>
      <Grid container spacing={2} sx={{ marginBottom: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <InputLabel id={"business-entity"}>Business Entity</InputLabel>
          <Autocomplete
            options={mappedBusinessEntityIDs}
            getOptionLabel={(option) => option?.name || ""}
            value={
              mappedBusinessEntityIDs.find(
                (item) => item.id === selectedBusinessEntityID,
              ) || null
            }
            onChange={(_, newValue) => {
              setSelectedBusinessEntityID(newValue ? newValue.id : null);
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={selectedUserDetailLoading || hrFiltersDataLoading}
            ListboxProps={{
              style: {
                width: "max-content",
                minWidth: "100%",
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label=""
                placeholder="Select Business Entity"
                size="small"
              />
            )}
            sx={{ width: 180 }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <InputLabel id={"model-database"}>Model Database</InputLabel>
          <Autocomplete
            options={mappedModelDatabases}
            getOptionLabel={(option) => option?.name || ""}
            value={
              mappedModelDatabases.find(
                (item) => item.id === selectedModelDatabase,
              ) || null
            }
            onChange={(_, newValue) => {
              setSelectedModelDatabase(newValue ? newValue.id : null);
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={selectedUserDetailLoading || hrFiltersDataLoading}
            ListboxProps={{
              style: {
                width: "max-content",
                minWidth: "100%",
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label=""
                placeholder="Select Model Database"
                size="small"
              />
            )}
            sx={{ width: 300 }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <InputLabel id={"scenario-mnemonics"}>Scenario Mnemonics</InputLabel>
          <Autocomplete
            options={mappedScenarioMnemonics}
            getOptionLabel={(option) => option?.name || ""}
            value={
              mappedScenarioMnemonics.find(
                (item) => item.id === selectedScenarioMnemonics,
              ) || null
            }
            onChange={(_, newValue) => {
              setSelectedScenarioMnemonics(newValue ? newValue.id : null);
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={selectedUserDetailLoading || hrFiltersDataLoading}
            ListboxProps={{
              style: {
                width: "max-content",
                minWidth: "100%",
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label=""
                placeholder="Select Scenario Mnemonics"
                size="small"
              />
            )}
            sx={{ width: 300 }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleSubmitBtn}
            sx={{
              fontSize: "0.8rem",
              boxShadow: "0px 2px 2px rgba(0, 0, 0, 0.1)",
              height: "38px",
              mt: 3,
            }}
            disabled={
              selectedUserDetailLoading ||
              hrFiltersDataLoading ||
              hrFiltersDataLoading ||
              sensitivityGapDataLoading ||
              !selectedBusinessEntityID ||
              !selectedModelDatabase ||
              !selectedScenarioMnemonics
            }
          >
            Submit
          </Button>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ marginBottom: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <InlineDropdown
            label="Show Figures In"
            labelId="show-figures-in"
            options={mappedFigureDivisors}
            selectedValue={selectedFigureDivisor}
            handleChange={setSelectedFigureDivisor}
            placeholder={"Show Figures In"}
            dropdownWidth={150}
            isLoading={
              selectedUserDetailLoading ||
              hrFiltersDataLoading ||
              sensitivityGapDataLoading
            }
          />
        </Grid>
        {localCurrency && (
          <Grid item xs={12} sm={6} md={4}>
            <InputLabel id="currency-type">Currency</InputLabel>
            <Typography
              sx={{
                color: "#6a6a6a",
                fontWeight: "bold",
                marginTop: 1,
              }}
            >
              {localCurrency}
            </Typography>
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={4}>
          <InlineDropdown
            label="Show Decimal In"
            labelId="show-decimal-in"
            options={mappedDecimalsIn}
            selectedValue={selectedDecimalsIn}
            handleChange={setSelectedDecimalsIn}
            placeholder={"Show Decimals In"}
            dropdownWidth={150}
            isLoading={
              selectedUserDetailLoading ||
              hrFiltersDataLoading ||
              sensitivityGapDataLoading
            }
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
          md={4}
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "flex-end",
          }}
        >
          <div className={classes.sensitivityGAPGlobalExportBtn}>
            <SensitivityGAPGlobalExport
              columns={exportColumns}
              datas={preprocessedDataExport}
              disabled={
                sensitivityGapDataLoading || !sensitivityGapData?.length
              }
            />
          </div>
        </Grid>

        <Grid size={{ xs: 12, md: 12, lg: 12 }}>
          {!sensitivityGapDataLoading && sensitivityGapDataError ? (
            <Item>
              <Typography
                variant="p"
                component="p"
                sx={{
                  color: "#adadad",
                  minHeight: "250px",
                }}
              >
                There was an error in fetching the data. Please try again later.
              </Typography>
            </Item>
          ) : (!sensitivityGapDataLoading && !sensitivityGapData) ||
            (!sensitivityGapDataLoading && !sensitivityGapData?.length) ? (
            <Item>
              <Typography
                variant="p"
                component="p"
                sx={{
                  color: "#adadad",
                  minHeight: "250px",
                }}
              >
                No data available for the selected filters.
              </Typography>
            </Item>
          ) : (
            <ColumnGroupingTable
              columns={columns}
              datas={preprocessedData}
              loading={sensitivityGapDataLoading}
              tableHeight={410}
              freezeParentColumnCount={1}
              applyThousandsSeparator={true}
              disablePagination
            />
          )}
        </Grid>
      </Grid>
    </Card>
  );
};

export default SensitivityGAPGlobalFiltersAndReports;
